import { Select, SelectProvider } from '@ariakit/react'
import type { FC } from 'react'
import { ButtonLegacy, type ButtonProps } from '@/components/ui/button-legacy'
import { CheckIcon, ChevronDownIcon } from '@/components/ui/icons'
import { SelectItem, SelectPopover } from '@/components/ui/select-atoms-legacy'
import { PhotosetImagesSearchParams } from '@/features/photoset/types'
import {
	orderByToLabel,
	orderDirectionToLabel
} from '@/features/photoset/utils'
import { objectKeys } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useTranslation } from '@/translations/i18n'

interface SelectTriggerButtonProps extends ButtonProps {
	$minWidth?: number
	$isPointer?: boolean
}

const SelectTriggerButton = ({
	$minWidth,
	$isPointer = true,
	className,
	style,
	...props
}: SelectTriggerButtonProps) => (
	<ButtonLegacy
		className={`justify-between border border-transparent px-3 no-underline hover:not-disabled:bg-transparent aria-expanded:border-neutral-300 aria-expanded:shadow-[0_0_0_4px_var(--color-neutral-200)] [&_svg]:fill-neutral-500 [&_svg]:transition-all [&_svg]:duration-200 [&_svg]:ease-in-out aria-expanded:[&_svg]:rotate-180 ${className ?? ''}`}
		style={{
			minWidth: $minWidth ?? 0,
			cursor: $isPointer ? 'pointer' : 'auto',
			...style
		}}
		type='button'
		{...props}
	/>
)

export const ImagesSortControls: FC<{ isReadonlyOrderBy?: boolean }> = ({
	isReadonlyOrderBy = false
}) => {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		PhotosetImagesSearchParams
	)

	return (
		<div className='relative flex'>
			<SelectProvider
				setValue={(value: PhotosetImagesSearchParams['order_by']) =>
					setSearchParams({ order_by: value })
				}
				value={searchParams.order_by}
			>
				{isReadonlyOrderBy ? (
					<SelectTriggerButton
						$isPointer={!isReadonlyOrderBy}
						$minWidth={135}
						type='button'
						variant='tertiary'
					>
						<div className='flex gap-2'>
							<span className='font-extralight text-neutral-600'>
								{t('common:order_by')}
							</span>
							{t(
								`photoset:order_by_options.${searchParams.order_by}`
							)}
						</div>
					</SelectTriggerButton>
				) : (
					<Select
						render={
							<SelectTriggerButton
								$isPointer={!isReadonlyOrderBy}
								$minWidth={135}
								type='button'
								variant='tertiary'
							>
								<div className='flex gap-2'>
									<span className='font-extralight text-neutral-600'>
										{t('common:order_by')}
									</span>
									{t(
										`photoset:order_by_options.${searchParams.order_by}`
									)}
								</div>
								<ChevronDownIcon className='fill-neutral-500' />
							</SelectTriggerButton>
						}
					/>
				)}

				<SelectPopover gutter={4} sameWidth>
					{objectKeys(orderByToLabel).map((option) => (
						<SelectItem
							className='flex items-center justify-between'
							key={option}
							value={option}
						>
							{t(orderByToLabel[option])}

							{option === searchParams.order_by && (
								<CheckIcon size={20} />
							)}
						</SelectItem>
					))}
				</SelectPopover>
			</SelectProvider>

			<SelectProvider
				setValue={(
					value: PhotosetImagesSearchParams['order_direction']
				) => setSearchParams({ order_direction: value })}
				value={searchParams.order_direction}
			>
				<Select
					render={
						<SelectTriggerButton
							$minWidth={135}
							type='button'
							variant='tertiary'
						>
							{t(
								`photoset:order_direction_options.${searchParams.order_direction}`
							)}
							<ChevronDownIcon className='fill-neutral-500' />
						</SelectTriggerButton>
					}
				/>

				<SelectPopover gutter={4} sameWidth>
					{objectKeys(orderDirectionToLabel).map((option) => (
						<SelectItem
							className='flex items-center gap-2'
							key={option}
							value={option}
						>
							<span>{t(orderDirectionToLabel[option])}</span>

							{option === searchParams.order_direction && (
								<CheckIcon size={20} />
							)}
						</SelectItem>
					))}
				</SelectPopover>
			</SelectProvider>
		</div>
	)
}

ImagesSortControls.displayName = 'ImagesSortControls'
